import React from "react";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { colors } from "../../theme/colors";

export type ToggleSize = "small" | "default" | "large";
export type ToggleVariant = "switch" | "segmented";

export interface ToggleProps {
  /** checked: true = ON, false = OFF, null = NEUTRAL */
  checked?: boolean | null;
  onChange?: (checked: boolean) => void;
  size?: ToggleSize;
  /** Toggle variant - 'switch' for sliding toggle, 'segmented' for ON/OFF/NEUTRAL buttons */
  variant?: ToggleVariant;
  label?: string;
  labelPosition?: "left" | "right";
  description?: string;
  disabled?: boolean;
  activeColor?: string;
  className?: string;
  wrapperClassName?: string;
}

// Switch variant size config
const switchSizeConfig = {
  small: {
    trackWidth: 42,
    trackHeight: 24,
    trackRadius: 8,
    knobSize: 18,
    knobRadius: 5,
    iconSize: 12,
    padding: 3,
  },
  default: {
    trackWidth: 52,
    trackHeight: 30,
    trackRadius: 10,
    knobSize: 22,
    knobRadius: 6,
    iconSize: 14,
    padding: 4,
  },
  large: {
    trackWidth: 72,
    trackHeight: 40,
    trackRadius: 14,
    knobSize: 32,
    knobRadius: 8,
    iconSize: 20,
    padding: 4,
  },
};

// Segmented variant size config
const segmentedSizeConfig = {
  small: {
    paddingX: 12,
    paddingY: 4,
    fontSize: 12,
    iconSize: 12,
    borderRadius: 4,
    gap: 4,
  },
  default: {
    paddingX: 16,
    paddingY: 6,
    fontSize: 14,
    iconSize: 14,
    borderRadius: 4,
    gap: 6,
  },
  large: {
    paddingX: 20,
    paddingY: 8,
    fontSize: 16,
    iconSize: 16,
    borderRadius: 6,
    gap: 8,
  },
};

export const Toggle: React.FC<ToggleProps> = ({
  checked = null,
  onChange,
  size = "default",
  variant = "switch",
  label,
  labelPosition = "right",
  description,
  disabled = false,
  activeColor = colors.teal,
  className,
  wrapperClassName,
}) => {
  // Determine state: true = ON, false = OFF, null = NEUTRAL
  const isOn = checked === true;
  const isOff = checked === false;

  const handleChange = (newValue: boolean) => {
    if (!disabled && onChange) {
      onChange(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === "Enter" || e.key === " ") && !disabled) {
      e.preventDefault();
      onChange?.(!checked);
    }
  };

  // Segmented ON/OFF button variant with neutral support
  const renderSegmented = () => {
    const config = segmentedSizeConfig[size];

    // Get colors based on disabled state
    const getOnButtonStyles = () => {
      if (disabled && isOn) {
        // Disabled ON state - muted teal appearance
        return {
          backgroundColor: colors.surfaceSubtle,
          color: colors.textMuted,
        };
      }
      return {
        backgroundColor: isOn ? activeColor : colors.surfaceLighter,
        color: isOn ? colors.white : colors.textMuted,
      };
    };

    const getOffButtonStyles = () => {
      if (disabled) {
        // Disabled OFF state - muted appearance
        return {
          backgroundColor: colors.surfaceLighter,
          color: colors.surfaceDark,
        };
      }
      return {
        backgroundColor: isOff ? colors.textDefault : colors.surfaceLighter,
        color: isOff ? colors.white : colors.textMuted,
      };
    };

    const onStyles = getOnButtonStyles();
    const offStyles = getOffButtonStyles();

    return (
      <div
        className={cn(
          "inline-flex overflow-hidden",
          disabled && "cursor-not-allowed",
          className
        )}
        style={{
          borderRadius: config.borderRadius,
          border: `1px solid ${colors.surfaceSubtle}`,
        }}
      >
        {/* ON Button */}
        <button
          type="button"
          onClick={() => handleChange(true)}
          disabled={disabled}
          className={cn(
            "flex items-center font-medium transition-colors",
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          style={{
            paddingLeft: config.paddingX,
            paddingRight: config.paddingX,
            paddingTop: config.paddingY,
            paddingBottom: config.paddingY,
            fontSize: config.fontSize,
            backgroundColor: onStyles.backgroundColor,
            color: onStyles.color,
            borderRight: `1px solid ${colors.surfaceSubtle}`,
            gap: config.gap,
          }}
        >
          ON
          {isOn && <Check size={config.iconSize} />}
        </button>
        {/* OFF Button */}
        <button
          type="button"
          onClick={() => handleChange(false)}
          disabled={disabled}
          className={cn(
            "flex items-center font-medium transition-colors",
            disabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          style={{
            paddingLeft: config.paddingX,
            paddingRight: config.paddingX,
            paddingTop: config.paddingY,
            paddingBottom: config.paddingY,
            fontSize: config.fontSize,
            backgroundColor: offStyles.backgroundColor,
            color: offStyles.color,
            gap: config.gap,
          }}
        >
          OFF
        </button>
      </div>
    );
  };

  // Switch variant (original sliding toggle)
  const renderSwitch = () => {
    const config = switchSizeConfig[size];

    // Calculate knob position: left for OFF/NEUTRAL, right for ON
    const getKnobLeft = () => {
      if (isOn) return config.trackWidth - config.knobSize - config.padding;
      return config.padding;
    };

    // Get background color based on state
    const getBackgroundColor = () => {
      if (isOn) return activeColor;
      if (isOff) return colors.surfaceDark;
      return colors.surfaceLight; // neutral
    };

    return (
      <button
        type="button"
        role="switch"
        aria-label={label}
        aria-checked={isOn}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => handleChange(!isOn)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          "relative inline-flex items-center transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
          className
        )}
        style={{
          width: config.trackWidth,
          height: config.trackHeight,
          borderRadius: config.trackRadius,
          backgroundColor: getBackgroundColor(),
        }}
      >
        {/* Knob */}
        <span
          className="absolute flex items-center justify-center bg-white transition-all duration-200"
          style={{
            width: config.knobSize,
            height: config.knobSize,
            borderRadius: config.knobRadius,
            top: config.padding,
            left: getKnobLeft(),
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
          }}
        >
          {isOn ? (
            <Check
              size={config.iconSize}
              strokeWidth={2.5}
              style={{ color: activeColor }}
            />
          ) : isOff ? (
            <X
              size={config.iconSize}
              strokeWidth={2.5}
              style={{ color: colors.surfaceDark }}
            />
          ) : null}
        </span>
      </button>
    );
  };

  const toggleElement =
    variant === "segmented" ? renderSegmented() : renderSwitch();

  if (!label) {
    return toggleElement;
  }

  return (
    <div
      className={cn(
        "flex items-start gap-3",
        labelPosition === "left" && "flex-row-reverse",
        wrapperClassName
      )}
    >
      <div className="shrink-0">{toggleElement}</div>
      <div className="flex flex-col">
        <span
          className="text-sm font-medium"
          style={{ color: disabled ? colors.textWeak : colors.textDefault }}
        >
          {label}
        </span>
        {description && (
          <span className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
            {description}
          </span>
        )}
      </div>
    </div>
  );
};

export default Toggle;
